import React, { useState } from 'react';
import { Box, Text, InfoIcon, TextButton } from '@wix/design-system';
import s from './styles.scss';
import { useTranslation } from 'react-i18next';
import { CurrentPlanDetails } from 'types';
import { usePremiumCurrencyFormatter } from '@wix/premium-react-text';
import { ViewCurrentPlanModal } from 'components/ViewPlanFeaturesModal';
import {
  BiLogger,
  FlowType,
  PaymentCycle_BIMap,
} from 'services/BiLogger/BiLoggerTypes';
import { CurrentPlanInOfferingMode } from 'enums';
import { ChangePlanModal } from 'components/ChangePlan/advanced/ChangePlanModal/ChangePlanModal';

type PurchaseMode = 'initialPurchase' | 'upgrade' | 'anonymous';

interface BannerContent {
  title: string;
  subtitle: string;
  tooltip?: string;
}

const bannerContentMap: { [key in PurchaseMode]: BannerContent } = {
  initialPurchase: {
    title: 'studio_package_picker.ed_x_migration.banner.title',
    subtitle: 'studio_package_picker.ed_x_migration.banner.subtitle',
    tooltip: 'studio_package_picker.ed_x_migration.banner.tooltip',
  },
  upgrade: {
    title: 'studio_package_picker.ed_x_migration.banner.title',
    subtitle:
      'studio_package_picker.ed_x_migration.banner.2nd_upgrade.subtitle',
  },
  anonymous: {
    title: 'studio_package_picker.ed_x_migration.banner.title',
    subtitle: 'studio_package_picker.ed_x_migration.banner.anon.subtitle',
  },
};

const getPurchaseMode = (
  isAnonymous: Boolean,
  isUpgrade: Boolean,
  isChangePlan: Boolean,
): PurchaseMode => {
  if (isAnonymous) {
    return 'anonymous';
  } else if (isUpgrade || isChangePlan) {
    return 'upgrade';
  }

  return 'initialPurchase';
};

export const StudioMigrationBanner = ({
  isAnonymous,
  isUpgrade,
  isChangePlan,
  currentPlanDetails,
  biLogger,
}: {
  isAnonymous: Boolean;
  isUpgrade: Boolean;
  isChangePlan: Boolean;
  currentPlanDetails?: CurrentPlanDetails;
  biLogger: BiLogger;
}) => {
  const [isViewPlanModalOpen, setViewPlanModalOpen] = useState(false);
  const { t } = useTranslation();
  const [currencyToString] = usePremiumCurrencyFormatter();

  const pricePerMonth = currentPlanDetails?.pricePerMonth
    ? currencyToString(currentPlanDetails?.pricePerMonth)
    : undefined;

  const purchaseMode = getPurchaseMode(isAnonymous, isUpgrade, isChangePlan);
  const bannerContent = bannerContentMap[purchaseMode];

  const openModal = () => {
    biLogger.logCurrentPlanModalOpened({
      changePlanType: CurrentPlanInOfferingMode.ProductNotMatch,
      currentProductId: currentPlanDetails?.planId!,
      currentCycleId: PaymentCycle_BIMap[currentPlanDetails?.paymentCycle!],
      packageId: currentPlanDetails?.userPackageGuid!,
      flowType: isUpgrade
        ? FlowType.Upgrade
        : isChangePlan
        ? FlowType.ChangePlan
        : FlowType.Regular,
    });

    setViewPlanModalOpen(true);
  };

  const closeModal = () => setViewPlanModalOpen(false);

  return (
    <Box
      direction='vertical'
      align='center'
      gap='2px'
      maxWidth={s.maxWidth}
      width='100%'
      backgroundColor={s.studioBackgroundColor}
      padding='18px 24px'
    >
      <Box verticalAlign='middle'>
        <Text weight='bold' light>
          {t(bannerContent.title)}
        </Text>
        {bannerContent.tooltip && (
          <InfoIcon
            className={s.infoIcon}
            size='small'
            content={t(bannerContent.tooltip)}
          />
        )}
      </Box>
      <Text light>
        {t(bannerContent.subtitle, {
          planName: currentPlanDetails?.planName,
          originalPlanPrice: pricePerMonth,
        })}
        {purchaseMode === 'upgrade' && (
          <Box inline paddingLeft='SP1'>
            <TextButton
              skin='light'
              onClick={openModal}
              dataHook='view-current-plan-btn'
              underline='always'
            >
              {t(
                'studio_package_picker.ed_x_migration.banner.2nd_upgrade.link.view_plan',
              )}
            </TextButton>
          </Box>
        )}
      </Text>
      {isViewPlanModalOpen && (
        <ViewCurrentPlanModal
          isOpen={isViewPlanModalOpen}
          closeModal={closeModal}
        />
      )}
      <ChangePlanModal />
    </Box>
  );
};
